import React, { useEffect, useState } from 'react';
import api from '../utils/api';
import { ChartBar, TrendUp, FilePdf, Spinner, ClipboardText, ListChecks } from '@phosphor-icons/react';
import { toast } from 'sonner';

export default function ReportsPage() {
  const [inspectionSummary, setInspectionSummary] = useState(null);
  const [defectTrends, setDefectTrends] = useState(null);
  const [loading, setLoading] = useState(true);
  const [generatingPdf, setGeneratingPdf] = useState(null);
  const [projects, setProjects] = useState([]);
  const [selectedProject, setSelectedProject] = useState('');

  useEffect(() => {
    loadReports();
    loadProjects();
  }, []);

  const loadReports = async () => {
    try {
      const [inspRes, defRes] = await Promise.all([
        api.get('/reports/inspection-summary'),
        api.get('/reports/defect-trends')
      ]);
      setInspectionSummary(inspRes.data);
      setDefectTrends(defRes.data);
    } catch (error) {
      toast.error('Failed to load reports');
    } finally {
      setLoading(false);
    }
  };

  const loadProjects = async () => {
    try {
      const response = await api.get('/apqp/projects');
      setProjects(response.data);
    } catch (error) {
      console.error('Failed to load projects');
    }
  };

  const handleGeneratePdf = async (reportType) => {
    setGeneratingPdf(reportType);
    try {
      const response = await api.post('/reports/generate-pdf', {
        report_type: reportType,
        project_id: selectedProject || null,
        document_ids: [],
        include_images: true
      }, {
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      const filename = reportType === 'ppap' 
        ? `PPAP_Report_${new Date().toISOString().split('T')[0]}.pdf`
        : `Inspection_Report_${new Date().toISOString().split('T')[0]}.pdf`;
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success('PDF report generated successfully');
    } catch (error) {
      toast.error('Failed to generate PDF report');
    } finally {
      setGeneratingPdf(null);
    }
  };

  if (loading) {
    return <div className="text-center py-12 text-gray-500">Loading reports...</div>;
  }

  return (
    <div data-testid="reports-page" className="space-y-6">
      <div>
        <h1 className="text-3xl font-bold tracking-tight text-gray-900">Reports & Analytics</h1>
        <p className="mt-1 text-sm text-gray-600">Quality metrics, trends, and PDF report generation</p>
      </div>

      {/* PDF Report Generation Section */}
      <div className="bg-white border border-gray-200 rounded-sm">
        <div className="border-b border-gray-200 p-6 bg-red-700">
          <div className="flex items-center gap-3">
            <FilePdf size={28} weight="fill" className="text-white" />
            <div>
              <h2 className="text-lg font-semibold text-white">Generate PDF Reports</h2>
              <p className="text-sm text-red-100">Download professional reports with ALU-FIN branding</p>
            </div>
          </div>
        </div>
        <div className="p-6">
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-900 mb-2">Select Project (Optional for PPAP)</label>
            <select
              value={selectedProject}
              onChange={(e) => setSelectedProject(e.target.value)}
              className="w-full max-w-md px-4 py-2 border border-gray-300 rounded-sm focus:ring-2 focus:ring-blue-600 focus:border-transparent"
            >
              <option value="">All Projects</option>
              {projects.map(p => (
                <option key={p.id} value={p.id}>{p.project_name} - {p.customer}</option>
              ))}
            </select>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="border border-gray-200 rounded-sm p-4">
              <div className="flex items-center gap-3 mb-3">
                <ClipboardText size={24} className="text-blue-600" />
                <h3 className="font-semibold text-gray-900">PPAP Submission Report</h3>
              </div>
              <p className="text-sm text-gray-600 mb-4">
                Generate a report of all PPAP submissions with status, levels, and comments.
              </p>
              <button
                data-testid="generate-ppap-pdf"
                onClick={() => handleGeneratePdf('ppap')}
                disabled={generatingPdf === 'ppap'}
                className="flex items-center justify-center gap-2 w-full px-4 py-2 bg-blue-600 text-white rounded-sm hover:bg-blue-700 transition-colors disabled:opacity-50"
              >
                {generatingPdf === 'ppap' ? (
                  <>
                    <Spinner size={18} className="animate-spin" />
                    Generating...
                  </>
                ) : (
                  <>
                    <FilePdf size={18} weight="bold" />
                    Download PPAP Report
                  </>
                )}
              </button>
            </div>
            
            <div className="border border-gray-200 rounded-sm p-4">
              <div className="flex items-center gap-3 mb-3">
                <ListChecks size={24} className="text-green-600" />
                <h3 className="font-semibold text-gray-900">Inspection Summary Report</h3>
              </div>
              <p className="text-sm text-gray-600 mb-4">
                Generate a summary of all quality inspections with pass rates by coating type.
              </p>
              <button
                data-testid="generate-inspection-pdf"
                onClick={() => handleGeneratePdf('inspection')}
                disabled={generatingPdf === 'inspection'}
                className="flex items-center justify-center gap-2 w-full px-4 py-2 bg-green-600 text-white rounded-sm hover:bg-green-700 transition-colors disabled:opacity-50"
              >
                {generatingPdf === 'inspection' ? (
                  <>
                    <Spinner size={18} className="animate-spin" />
                    Generating...
                  </>
                ) : (
                  <>
                    <FilePdf size={18} weight="bold" />
                    Download Inspection Report
                  </>
                )}
              </button>
            </div>
          </div>
          
          <p className="mt-4 text-xs text-gray-500">
            Note: For APQP Project Reports with document previews, go to the specific project detail page and click "Generate PDF Report".
          </p>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="bg-white border border-gray-200 rounded-sm">
          <div className="border-b border-gray-200 p-6 flex items-center gap-3">
            <ChartBar size={24} weight="duotone" className="text-blue-600" />
            <h2 className="text-lg font-semibold text-gray-900">Inspection Summary</h2>
          </div>
          <div className="p-6">
            <div className="mb-4">
              <p className="text-sm text-gray-600">Total Inspections</p>
              <p className="text-3xl font-bold text-gray-900 mono mt-1">{inspectionSummary?.total_inspections || 0}</p>
            </div>
            <div className="space-y-3">
              {inspectionSummary?.by_coating_type && Object.entries(inspectionSummary.by_coating_type).map(([coating, data]) => (
                <div key={coating} className="border border-gray-200 rounded-sm p-4">
                  <p className="text-sm font-medium text-gray-900 mb-2 capitalize">{coating.replace('_', ' ')}</p>
                  <div className="grid grid-cols-4 gap-2 text-center">
                    <div>
                      <p className="text-xs text-gray-500">Total</p>
                      <p className="text-lg font-semibold text-gray-900 mono">{data.total}</p>
                    </div>
                    <div>
                      <p className="text-xs text-gray-500">Pass</p>
                      <p className="text-lg font-semibold text-green-600 mono">{data.pass}</p>
                    </div>
                    <div>
                      <p className="text-xs text-gray-500">Fail</p>
                      <p className="text-lg font-semibold text-red-600 mono">{data.fail}</p>
                    </div>
                    <div>
                      <p className="text-xs text-gray-500">Rework</p>
                      <p className="text-lg font-semibold text-purple-600 mono">{data.rework}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        <div className="bg-white border border-gray-200 rounded-sm">
          <div className="border-b border-gray-200 p-6 flex items-center gap-3">
            <TrendUp size={24} weight="duotone" className="text-red-600" />
            <h2 className="text-lg font-semibold text-gray-900">Defect Trends</h2>
          </div>
          <div className="p-6 space-y-6">
            <div>
              <p className="text-sm text-gray-600 mb-3">By Severity</p>
              <div className="space-y-2">
                <div className="flex items-center justify-between p-3 bg-red-50 border border-red-200 rounded-sm">
                  <span className="text-sm font-medium text-gray-900">Critical</span>
                  <span className="text-lg font-bold text-red-600 mono">{defectTrends?.by_severity?.critical || 0}</span>
                </div>
                <div className="flex items-center justify-between p-3 bg-orange-50 border border-orange-200 rounded-sm">
                  <span className="text-sm font-medium text-gray-900">Major</span>
                  <span className="text-lg font-bold text-orange-600 mono">{defectTrends?.by_severity?.major || 0}</span>
                </div>
                <div className="flex items-center justify-between p-3 bg-yellow-50 border border-yellow-200 rounded-sm">
                  <span className="text-sm font-medium text-gray-900">Minor</span>
                  <span className="text-lg font-bold text-yellow-600 mono">{defectTrends?.by_severity?.minor || 0}</span>
                </div>
              </div>
            </div>

            <div>
              <p className="text-sm text-gray-600 mb-3">By Status</p>
              <div className="grid grid-cols-2 gap-3">
                <div className="p-3 border border-gray-200 rounded-sm text-center">
                  <p className="text-xs text-gray-500">Open</p>
                  <p className="text-2xl font-bold text-gray-900 mono mt-1">{defectTrends?.by_status?.open || 0}</p>
                </div>
                <div className="p-3 border border-gray-200 rounded-sm text-center">
                  <p className="text-xs text-gray-500">In Progress</p>
                  <p className="text-2xl font-bold text-blue-600 mono mt-1">{defectTrends?.by_status?.['in-progress'] || 0}</p>
                </div>
                <div className="p-3 border border-gray-200 rounded-sm text-center">
                  <p className="text-xs text-gray-500">Resolved</p>
                  <p className="text-2xl font-bold text-green-600 mono mt-1">{defectTrends?.by_status?.resolved || 0}</p>
                </div>
                <div className="p-3 border border-gray-200 rounded-sm text-center">
                  <p className="text-xs text-gray-500">Closed</p>
                  <p className="text-2xl font-bold text-gray-600 mono mt-1">{defectTrends?.by_status?.closed || 0}</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
